// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

import React, { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import ProgressBar from './ProgressBar';
import './PlanetExploration.css';
import Button from './Button';
import AlienEncounterModal from './AlienEncounterModal';
import { useCredits } from '../contexts/CreditsContext';
import { useWeapons } from '../contexts/WeaponsContext';
import { useUniverse } from '../contexts/UniverseContext';
import { getUniverseLocationsForThreat, getUniverseExplorationTitle } from '../config/UniverseExplorationConfig';
import AlienManager from '../utils/game/AlienManager';

const PlanetExploration = ({ isOpen, onClose, planetData, onExplorationComplete }) => {
  // Use Context API for credits and universe
  const { addCredits, removeCredits } = useCredits();
  const { getCurrentUniverseData, getCurrentUniverseInfo } = useUniverse();
  const { canPurchaseWeapon, purchaseWeapon } = useWeapons();
  
  const [explorationPhase, setExplorationPhase] = useState('landing'); // landing, exploring, result, departing
  const [showAlienModal, setShowAlienModal] = useState(false);
  const [landingProgress, setLandingProgress] = useState(0);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [explorationResult, setExplorationResult] = useState(null);
  const [terrain, setTerrain] = useState([]);
  const [shuttlePosition, setShuttlePosition] = useState({ x: 50, y: 10 });
  const [stars, setStars] = useState([]);
  const [explorationLocations, setExplorationLocations] = useState([]);
  const [exploredLocationIds, setExploredLocationIds] = useState([]);
  const [encounterAlien, setEncounterAlien] = useState(null);
  const [planetAliens, setPlanetAliens] = useState([]);
  
  const intervalRef = useRef(null);

  // Generate terrain, stars, and locations on mount
  useEffect(() => {
    if (isOpen) {
      // Reset state when opening
      setExplorationPhase('landing');
      setLandingProgress(0);
      setSelectedLocation(null);
      setExplorationResult(null);
      setShuttlePosition({ x: 50, y: 10 });
      setExploredLocationIds([]);
      setEncounterAlien(null);

      // Get aliens for this planet
      const universeData = getCurrentUniverseData();
      const universeAliens = universeData?.aliens || null;
      if (universeAliens && planetData?.id !== undefined) {
        const aliens = AlienManager.getAliensForPlanet(planetData.id, universeAliens);
        setPlanetAliens(aliens);
      } else {
        setPlanetAliens([]);
      }

      // Get 6 random locations based on planet threat level and current universe
      const threatLevel = planetData?.threat || 'MODERATE';
      const universeInfo = getCurrentUniverseInfo();
      const universeId = universeInfo?.id || 'milkyway';
      const locations = getUniverseLocationsForThreat(universeId, threatLevel);
      setExplorationLocations(locations);

      // Generate starfield
      const newStars = [];
      for (let i = 0; i < 100; i++) {
        newStars.push({
          x: Math.random() * 100,
          y: Math.random() * 60,
          size: Math.random() * 2 + 1,
          opacity: Math.random() * 0.5 + 0.5
        });
      }
      setStars(newStars);

      // Generate terrain
      const newTerrain = [];
      for (let i = 0; i <= 100; i += 2) {
        const height = 60 + Math.sin(i * 0.1) * 10 + Math.random() * 5;
        newTerrain.push({ x: i, y: height });
      }
      setTerrain(newTerrain);
    }
  }, [isOpen, planetData, getCurrentUniverseData, getCurrentUniverseInfo]);

  // Landing animation
  useEffect(() => {
    if (explorationPhase === 'landing') {
      intervalRef.current = setInterval(() => {
        setLandingProgress(prev => {
          if (prev >= 100) {
            clearInterval(intervalRef.current);
            setExplorationPhase('exploring');
            return 100;
          }
          return prev + 2;
        });

        // Animate shuttle descent
        setShuttlePosition(prev => ({
          x: prev.x,
          y: Math.min(55, 10 + (landingProgress / 100) * 45)
        }));
      }, 50);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [explorationPhase, landingProgress]);

  // Departure animation
  useEffect(() => {
    if (explorationPhase === 'departing') {
      intervalRef.current = setInterval(() => {
        setLandingProgress(prev => {
          if (prev <= 0) {
            clearInterval(intervalRef.current);
            // Close modal after departure complete
            onClose();
            return 0;
          }
          return prev - 2;
        });

        // Animate shuttle ascent (reverse of landing)
        setShuttlePosition(prev => ({
          x: prev.x,
          y: Math.max(10, 55 - ((100 - landingProgress) / 100) * 45)
        }));
      }, 50);

      return () => {
        if (intervalRef.current) {
          clearInterval(intervalRef.current);
        }
      };
    }
  }, [explorationPhase, landingProgress, onClose]);

  const handleLocationSelect = (location) => {
    // Don't allow selecting already explored locations
    if (exploredLocationIds.includes(location.id)) {
      return;
    }

    setSelectedLocation(location);
    
    // Mark this location as explored
    setExploredLocationIds(prev => [...prev, location.id]);
    
    // Check for alien encounter (30% chance if aliens present)
    if (planetAliens.length > 0 && Math.random() < 0.3) {
      // Random alien encounter using AlienManager
      const randomAlien = planetAliens[Math.floor(Math.random() * planetAliens.length)];
      const encounter = AlienManager.generateEncounter(randomAlien.id, {
        encounterType: 'exploration',
        location: 'planet_surface'
      });
      
      if (encounter) {
        setEncounterAlien(encounter);
        setShowAlienModal(true);
        return; // Don't process location outcome yet
      }
    }
    
    // No alien encounter, process location outcome
    processLocationOutcome(location);
  };

  const handleAlienModalClose = () => {
    setShowAlienModal(false);
    setEncounterAlien(null);
  };

  const handleAlienOutcome = (outcome) => {
    // Outcome already processed by AlienEncounterModal
    // Just process the location outcome now
    if (selectedLocation) {
      processLocationOutcome(selectedLocation);
    }
  };

  const processLocationOutcome = (location) => {
    // Determine outcome based on chances
    const roll = Math.random();
    let cumulativeChance = 0;
    let selectedOutcome = location.outcomes[0];
    
    for (const outcome of location.outcomes) {
      cumulativeChance += outcome.chance;
      if (roll <= cumulativeChance) {
        selectedOutcome = outcome;
        break;
      }
    }
    
    // Apply credits change
    if (selectedOutcome.credits !== 0) {
      if (selectedOutcome.credits > 0) {
        addCredits(selectedOutcome.credits, 'planet exploration');
      } else {
        removeCredits(Math.abs(selectedOutcome.credits), 'planet exploration hazard');
      }
    }
    
    // Handle weapon discovery
    if (selectedOutcome.weapon) {
      const weaponCheck = canPurchaseWeapon(selectedOutcome.weapon);
      if (weaponCheck.canPurchase) {
        // Find first empty slot
        const emptySlotIndex = weaponCheck.emptySlotIndex;
        if (emptySlotIndex !== -1) {
          purchaseWeapon(selectedOutcome.weapon, emptySlotIndex);
          // Add weapon info to result for display
          selectedOutcome.weaponFound = weaponCheck.weapon;
        }
      }
      // If no slots available, weapon is lost (mentioned in message)
    }
    
    setExplorationResult(selectedOutcome);
    setExplorationPhase('result');
    
    // Notify parent
    if (onExplorationComplete) {
      onExplorationComplete(selectedOutcome);
    }
  };

  const handleContinueExploring = () => {
    // Return to location selection without resetting landing
    setExplorationPhase('exploring');
    setSelectedLocation(null);
    setExplorationResult(null);
  };

  const handleClose = () => {
    // Start departure animation instead of closing immediately
    setExplorationPhase('departing');
    setSelectedLocation(null);
    setExplorationResult(null);
  };

  if (!isOpen) return null;

  const getPlanetBgImage = () => {
    const type = (planetData?.type || 'barren').toLowerCase();
    const typeMap = {
      planet: 'terrestrial', station: 'station', blackhole: 'barren', warpgate: 'station',
    };
    const filename = typeMap[type] || (
      ['terrestrial','ocean','desert','ice','volcanic','jungle','gas_giant','barren','station'].includes(type)
        ? type : 'barren'
    );
    const universeInfo = getCurrentUniverseInfo();
    const universeId = universeInfo?.id || 'milkyway';
    // Use universe-specific image if it exists, fall back to generic
    return `url(/images/planets/${universeId}/${filename}.png), url(/images/planets/${filename}.png)`;
  };

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'LOW': return '#00ff88';
      case 'MODERATE': return '#ffaa00';
      case 'HIGH': return '#ff4444';
      case 'EXTREME': return '#ff00ff';
      default: return '#00ffff';
    }
  };

  return (
    <>
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={(() => {
        const universeInfo = getCurrentUniverseInfo();
        const universeId = universeInfo?.id || 'milkyway';
        const titleInfo = getUniverseExplorationTitle(universeId);
        return `${titleInfo.icon} ${titleInfo.title}`;
      })()}
      size="large"
      className="planet-exploration-modal"
    >
      <div className="exploration-planet-name">
        {planetData?.name || 'Unknown Planet'}
      </div>

      {/* Main Content */}
      <div className="exploration-content">
          {/* Landing Phase */}
          {explorationPhase === 'landing' && (
            <div className="exploration-landing">
              <div className="planet-surface" style={{ backgroundImage: getPlanetBgImage() }}>
                {/* Starfield */}
                {stars.map((star, i) => (
                  <div
                    key={i}
                    className="surface-star"
                    style={{
                      left: `${star.x}%`,
                      top: `${star.y}%`,
                      width: `${star.size}px`,
                      height: `${star.size}px`,
                      opacity: star.opacity
                    }}
                  />
                ))}

                {/* Terrain */}
                <svg className="terrain-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="terrainGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={planetData?.color || '#8B4513'} stopOpacity="0.8" />
                      <stop offset="100%" stopColor={planetData?.color || '#654321'} stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,100 L 0,${terrain[0]?.y || 70} ${terrain.map(p => `L ${p.x},${p.y}`).join(' ')} L 100,100 Z`}
                    fill="url(#terrainGradient)"
                    stroke={planetData?.color || '#8B4513'}
                    strokeWidth="0.5"
                  />
                </svg>

                {/* Shuttle */}
                <div
                  className="exploration-shuttle"
                  style={{
                    left: `${shuttlePosition.x}%`,
                    top: `${shuttlePosition.y}%`
                  }}
                >
                  🚀
                </div>

                {/* Landing Progress */}
                <div className="landing-status">
                  <div className="landing-text">LANDING SHUTTLE</div>
                  <ProgressBar 
                    value={landingProgress}
                    max={100}
                    color="success"
                    showLabel={true}
                    animated={true}
                    size="medium"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Departing Phase */}
          {explorationPhase === 'departing' && (
            <div className="exploration-landing">
              <div className="planet-surface" style={{ backgroundImage: getPlanetBgImage() }}>
                {/* Starfield */}
                {stars.map((star, i) => (
                  <div
                    key={i}
                    className="surface-star"
                    style={{
                      left: `${star.x}%`,
                      top: `${star.y}%`,
                      width: `${star.size}px`,
                      height: `${star.size}px`,
                      opacity: star.opacity
                    }}
                  />
                ))}

                {/* Terrain */}
                <svg className="terrain-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="terrainGradient" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" stopColor={planetData?.color || '#8B4513'} stopOpacity="0.8" />
                      <stop offset="100%" stopColor={planetData?.color || '#654321'} stopOpacity="1" />
                    </linearGradient>
                  </defs>
                  <path
                    d={`M 0,100 L 0,${terrain[0]?.y || 70} ${terrain.map(p => `L ${p.x},${p.y}`).join(' ')} L 100,100 Z`}
                    fill="url(#terrainGradient)"
                    stroke={planetData?.color || '#8B4513'}
                    strokeWidth="0.5"
                  />
                </svg>

                {/* Shuttle */}
                <div
                  className="exploration-shuttle"
                  style={{
                    left: `${shuttlePosition.x}%`,
                    top: `${shuttlePosition.y}%`
                  }}
                >
                  🚀
                </div>

                {/* Departure Progress */}
                <div className="landing-status">
                  <div className="landing-text">DEPARTING SHUTTLE</div>
                  <ProgressBar 
                    value={landingProgress}
                    max={100}
                    color="warning"
                    showLabel={true}
                    animated={true}
                    size="medium"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Exploration Phase */}
          {explorationPhase === 'exploring' && (
            <div className="exploration-locations" style={{ backgroundImage: getPlanetBgImage() }}>
              <div className="exploration-instructions">
                Select a location to explore. Each location can only be explored once per visit.
              </div>
              
              <div className="locations-grid">
                {explorationLocations.map(location => {
                  const isExplored = exploredLocationIds.includes(location.id);
                  return (
                    <div
                      key={location.id}
                      className={`location-card ${isExplored ? 'explored' : ''}`}
                      onClick={() => handleLocationSelect(location)}
                      style={{
                        opacity: isExplored ? 0.5 : 1,
                        cursor: isExplored ? 'not-allowed' : 'pointer'
                      }}
                    >
                      <div className="location-icon">{location.icon}</div>
                      <div className="location-name">
                        {location.name}
                        {isExplored && ' ✓'}
                      </div>
                      <div className="location-description">{location.description}</div>
                      <div
                        className="location-risk"
                        style={{ color: getRiskColor(location.risk) }}
                      >
                        {isExplored ? 'EXPLORED' : `RISK: ${location.risk}`}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Result Phase */}
          {explorationPhase === 'result' && explorationResult && (
            <div className="exploration-result">
              <div
                className="result-icon"
                style={{
                  color: explorationResult.type === 'positive' ? '#00ff88' :
                         explorationResult.type === 'negative' ? '#ff4444' : '#00ffff'
                }}
              >
                {explorationResult.icon}
              </div>
              
              <div className="result-location">
                {selectedLocation?.icon} {selectedLocation?.name}
              </div>
              
              <div
                className="result-message"
                style={{
                  color: explorationResult.type === 'positive' ? '#00ff88' :
                         explorationResult.type === 'negative' ? '#ff4444' : '#00ffff'
                }}
              >
                {explorationResult.message}
              </div>
              
              {explorationResult.credits !== 0 && (
                <div
                  className="result-fuel"
                  style={{
                    color: explorationResult.credits > 0 ? '#00ff88' : '#ff4444'
                  }}
                >
                  {explorationResult.credits > 0 ? '+' : ''}{explorationResult.credits} CREDITS
                </div>
              )}
              
              {explorationResult.weaponFound && (
                <div
                  className="result-fuel"
                  style={{
                    color: '#00ffff',
                    fontSize: '1.8em',
                    marginTop: '15px'
                  }}
                >
                  ⚔️ {explorationResult.weaponFound.name} ACQUIRED!
                </div>
              )}
              
              <Button 
                variant="primary"
                className="result-continue-btn" 
                onClick={handleContinueExploring}
              >
                EXPLORE ANOTHER LOCATION
              </Button>
            </div>
          )}
        </div>
    </Modal>

    {/* Alien Encounter Modal */}
    {showAlienModal && encounterAlien && (
      <AlienEncounterModal
        encounter={encounterAlien}
        onClose={handleAlienModalClose}
        onOutcome={handleAlienOutcome}
      />
    )}
  </>
  );
};

export default PlanetExploration;
